import React, { useEffect, useState } from 'react';
import { 
  Card, Table, Button, Form, Input, Switch, Space, Modal, 
  InputNumber, Select, Tabs, Typography, Divider, Tag, Tooltip 
} from 'antd';
import { 
  PlusOutlined, EditOutlined, SaveOutlined, CloseOutlined, 
  CheckOutlined, DeleteOutlined, QuestionCircleOutlined 
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import useLocationConfig from '../hooks/useLocationConfig';
import { LocationConfigDTO, LocationConfigJson } from '@/shared/api/model';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface LocationConfigFormValues {
  configName: string;
  entityType: string;
  addressType: string;
  requiredLevels: any[];
  gpsRequired: boolean;
  gpsAccuracyThreshold: number;
  allowInactiveLocations: boolean;
  requirePostalCode: boolean;
  requireHouseNumber: boolean;
  isActive: boolean;
  isMandatory: boolean;
}

const LocationConfigurationView: React.FC = () => {
  const { dark } = useTheme();
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [showInactive, setShowInactive] = useState(false);
  
  const {
    configs,
    selectedConfig,
    isLoading,
    configForm,
    isEditMode,
    fetchConfigsByEntityType,
    fetchConfigByName,
    setConfigForm,
    setIsEditMode,
    saveConfig,
    resetConfigForm
  } = useLocationConfig();

  // Load configs on component mount
  useEffect(() => {
    fetchConfigsByEntityType('ALL', showInactive);
  }, [fetchConfigsByEntityType, showInactive]);

  // Update form when configForm changes
  useEffect(() => {
    form.setFieldsValue({
      configName: configForm.configName,
      entityType: configForm.entityType,
      addressType: configForm.addressType,
      isActive: configForm.isActive !== undefined ? configForm.isActive : true,
      isMandatory: configForm.isMandatory !== undefined ? configForm.isMandatory : false,
      
      // Properly map nested JSON fields to flat form fields
      gpsRequired: configForm.configJson?.gpsCoordinates?.required,
      gpsAccuracyThreshold: configForm.configJson?.gpsCoordinates?.accuracyThreshold,
      allowInactiveLocations: configForm.configJson?.validation?.allowInactiveLocations,
      requirePostalCode: configForm.configJson?.validation?.requirePostalCode,
      requireHouseNumber: configForm.configJson?.validation?.requireHouseNumber,
      
      // Add requiredLevels separately to avoid overwriting with spread operator
      requiredLevels: configForm.configJson?.requiredLevels
    });
  }, [configForm, form]);

  const handleViewConfig = (config: LocationConfigDTO) => {
    if(config?.configName) {
      fetchConfigByName(config.configName);
      setIsModalVisible(true);

    }
  };

  const handleAddNew = () => {
    resetConfigForm();
    setIsEditMode(false);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    resetConfigForm();
  };

  const handleFormChange = (changedValues: LocationConfigFormValues) => {
    // Extract and structure form values into configForm format
    const { configName, entityType, addressType, isActive, isMandatory, ...jsonFields } = changedValues;
    
    if (configName !== undefined || entityType !== undefined || addressType !== undefined || 
        isActive !== undefined || isMandatory !== undefined) {
      setConfigForm({
        configName: configName !== undefined ? configName : configForm.configName,
        entityType: entityType !== undefined ? entityType : configForm.entityType,
        addressType: addressType !== undefined ? (addressType || '') : (configForm.addressType || ''),
        isActive: isActive !== undefined ? isActive : configForm.isActive,
        isMandatory: isMandatory !== undefined ? isMandatory : configForm.isMandatory
      });
    }
    
    // Handle JSON fields
    const updatedJson = { ...configForm.configJson };
    
    // Update requiredLevels - properly merge the changed level with existing levels
    if (jsonFields.requiredLevels) {
      // Find the index of the level that changed
      const changedLevelIndex = Object.keys(jsonFields.requiredLevels).find(
        // @ts-ignore
        key => typeof jsonFields.requiredLevels[key]?.mandatory !== 'undefined'
      );
      
      if (changedLevelIndex) {
        // Initialize requiredLevels if it doesn't exist
        updatedJson.requiredLevels = updatedJson.requiredLevels || [];
        
        // Get the level that changed
        const levelIndex = parseInt(changedLevelIndex);
        const changedLevel = jsonFields.requiredLevels[levelIndex];
        
        // Ensure the level exists in the array
        if (!updatedJson.requiredLevels[levelIndex]) {
          // Create the level object with default values
          updatedJson.requiredLevels[levelIndex] = {
            level: changedLevel.level,
            dirId: changedLevel.dirId || null,
            mandatory: changedLevel.mandatory
          };
        } else {
          // Update only the changed property (mandatory) and preserve other properties
          updatedJson.requiredLevels[levelIndex] = {
            ...updatedJson.requiredLevels[levelIndex],
            mandatory: changedLevel.mandatory
          };
        }
      }
    }

    // Update GPS coordinates settings
    if (jsonFields.gpsRequired !== undefined || jsonFields.gpsAccuracyThreshold !== undefined) {
      updatedJson.gpsCoordinates = updatedJson.gpsCoordinates || {};
      updatedJson.gpsCoordinates.required = jsonFields.gpsRequired !== undefined ? jsonFields.gpsRequired : (updatedJson.gpsCoordinates?.required || false);
      updatedJson.gpsCoordinates.accuracyThreshold = jsonFields.gpsAccuracyThreshold !== undefined ? jsonFields.gpsAccuracyThreshold : (updatedJson.gpsCoordinates?.accuracyThreshold || 50);
    }
    
    // Update validation settings
    if (jsonFields.allowInactiveLocations !== undefined || jsonFields.requirePostalCode !== undefined || jsonFields.requireHouseNumber !== undefined) {
      updatedJson.validation = updatedJson.validation || {};
      updatedJson.validation.allowInactiveLocations = jsonFields.allowInactiveLocations !== undefined ? jsonFields.allowInactiveLocations : (updatedJson.validation?.allowInactiveLocations || false);
      updatedJson.validation.requirePostalCode = jsonFields.requirePostalCode !== undefined ? jsonFields.requirePostalCode : (updatedJson.validation?.requirePostalCode || false);
      updatedJson.validation.requireHouseNumber = jsonFields.requireHouseNumber !== undefined ? jsonFields.requireHouseNumber : (updatedJson.validation?.requireHouseNumber || false);
    }
    
    if (Object.keys(jsonFields).length > 0) {
      setConfigForm({ configJson: updatedJson });
    }
  };

  const handleFormSubmit = () => {
    form.validateFields().then(() => {
      saveConfig();
      setIsModalVisible(false);
    });
  };

  // Table columns for location configurations
  const columns = [
    {
      title: 'Name',
      dataIndex: 'configName',
      key: 'configName',
      render: (text: string, record: LocationConfigDTO) => (
        <a onClick={() => handleViewConfig(record)}>{text}</a>
      ),
    },
    {
      title: 'Entity Type',
      dataIndex: 'entityType',
      key: 'entityType',
    },
    {
      title: 'Address Type',
      dataIndex: 'addressType',
      key: 'addressType',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: LocationConfigDTO) => (
        <Tag color={record.isActive ? 'green' : 'red'}>
          {record.isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Mandatory',
      key: 'mandatory',
      render: (_: any, record: LocationConfigDTO) => (
        <Tag color={record.isMandatory ? 'blue' : 'default'}>
          {record.isMandatory ? 'Required' : 'Optional'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: LocationConfigDTO) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              type="text"
              onClick={() => handleViewConfig(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <Typography.Title level={2}>Location Configuration</Typography.Title>
      </div>

      <Card
        title={
          <div className="flex justify-between items-center">
            <span>Location Configurations</span>
            <Space>
              <Switch
                checkedChildren="Show All"
                unCheckedChildren="Show Active Only"
                checked={showInactive}
                onChange={(checked) => {
                  setShowInactive(checked);
                }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddNew}
              >
                Add New Configuration
              </Button>
            </Space>
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={configs}
          rowKey="configName"
          loading={isLoading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={isEditMode ? "Edit Location Configuration" : "Create Location Configuration"}
        open={isModalVisible}
        width={700}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleFormSubmit}
            loading={isLoading}
          >
            Save
          </Button>
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleFormChange}
        >
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="Basic Settings" key="1">
              <Form.Item
                name="configName"
                label="Configuration Name"
                rules={[{ required: true, message: 'Configuration name is required' }]}
              >
                <Input 
                  placeholder="e.g., customer_residential_address"
                  disabled={isEditMode}
                />
              </Form.Item>
              
              <Form.Item
                name="entityType"
                label="Entity Type"
                rules={[{ required: true, message: 'Entity type is required' }]}
              >
                <Select placeholder="Select entity type">
                  <Select.Option value="CUSTOMER">Customer</Select.Option>
                  <Select.Option value="BRANCH">Branch</Select.Option>
                  <Select.Option value="AGENT">Agent</Select.Option>
                </Select>
              </Form.Item>
              
              <Form.Item
                name="addressType"
                label="Address Type"
                rules={[{ required: true, message: 'Address type is required' }]}
              >
                <Select placeholder="Select address type">
                  <Select.Option value="RESIDENTIAL">Residential</Select.Option>
                  <Select.Option value="BUSINESS">Business</Select.Option>
                  <Select.Option value="POSTAL">Postal</Select.Option>
                  <Select.Option value="BRANCH">Branch</Select.Option>
                </Select>
              </Form.Item>

              <Divider />
              
              <Form.Item
                name="isActive"
                label="Active"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              
              <Form.Item
                name="isMandatory"
                label="Mandatory"
                valuePropName="checked"
                tooltip="When enabled, this configuration must be used for the entity type"
              >
                <Switch />
              </Form.Item>
            </TabPane>
            
            <TabPane tab="Location Levels" key="2">
              <Title level={5}>Required Location Levels</Title>
              <Text type="secondary" className="block mb-4">
                Configure which location levels are required for this configuration
              </Text>
              
              <Form.Item
                name={['requiredLevels', 0, 'mandatory']}
                valuePropName="checked"
                label={
                  <Space>
                    <span>Zone</span>
                    <Form.Item name={['requiredLevels', 0, 'dirId']} noStyle>
                      <Input type="hidden" />
                    </Form.Item>
                    <Form.Item name={['requiredLevels', 0, 'level']} noStyle>
                      <Input type="hidden" defaultValue="ZONE" />
                    </Form.Item>
                  </Space>
                }
              >
                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
              </Form.Item>
              
              <Form.Item
                name={['requiredLevels', 1, 'mandatory']}
                valuePropName="checked"
                label={
                  <Space>
                    <span>Region</span>
                    <Form.Item name={['requiredLevels', 1, 'dirId']} noStyle>
                      <Input type="hidden" />
                    </Form.Item>
                    <Form.Item name={['requiredLevels', 1, 'level']} noStyle>
                      <Input type="hidden" defaultValue="REGION" />
                    </Form.Item>
                  </Space>
                }
              >
                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
              </Form.Item>
              
              <Form.Item
                name={['requiredLevels', 2, 'mandatory']}
                valuePropName="checked"
                label={
                  <Space>
                    <span>District</span>
                    <Form.Item name={['requiredLevels', 2, 'dirId']} noStyle>
                      <Input type="hidden" />
                    </Form.Item>
                    <Form.Item name={['requiredLevels', 2, 'level']} noStyle>
                      <Input type="hidden" defaultValue="DISTRICT" />
                    </Form.Item>
                  </Space>
                }
              >
                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
              </Form.Item>
              
              <Form.Item
                name={['requiredLevels', 3, 'mandatory']}
                valuePropName="checked"
                label={
                  <Space>
                    <span>Ward</span>
                    <Form.Item name={['requiredLevels', 3, 'dirId']} noStyle>
                      <Input type="hidden" />
                    </Form.Item>
                    <Form.Item name={['requiredLevels', 3, 'level']} noStyle>
                      <Input type="hidden" defaultValue="WARD" />
                    </Form.Item>
                  </Space>
                }
              >
                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
              </Form.Item>
              
              <Form.Item
                name={['requiredLevels', 4, 'mandatory']}
                valuePropName="checked"
                label={
                  <Space>
                    <span>Street</span>
                    <Form.Item name={['requiredLevels', 4, 'dirId']} noStyle>
                      <Input type="hidden" />
                    </Form.Item>
                    <Form.Item name={['requiredLevels', 4, 'level']} noStyle>
                      <Input type="hidden" defaultValue="STREET" />
                    </Form.Item>
                  </Space>
                }
              >
                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
              </Form.Item>
            </TabPane>
            
            <TabPane tab="GPS & Validation" key="3">
              <Title level={5}>GPS Coordinates</Title>
              <Divider className="my-2" />
              
              <Form.Item
                name="gpsRequired"
                valuePropName="checked"
                label="Require GPS Coordinates"
              >
                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
              </Form.Item>
              
              <Form.Item
                name="gpsAccuracyThreshold"
                label={
                  <Space>
                    <span>Accuracy Threshold (meters)</span>
                    <Tooltip title="Maximum allowed GPS accuracy in meters. Lower values are more precise.">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                }
              >
                <InputNumber min={1} max={1000} defaultValue={50} />
              </Form.Item>
              
              <Title level={5} className="mt-4">Validation Settings</Title>
              <Divider className="my-2" />
              
              <Form.Item
                name="allowInactiveLocations"
                valuePropName="checked"
                label="Allow Inactive Locations"
              >
                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
              </Form.Item>
              
              <Form.Item
                name="requirePostalCode"
                valuePropName="checked"
                label="Require Postal Code"
              >
                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
              </Form.Item>
              
              <Form.Item
                name="requireHouseNumber"
                valuePropName="checked"
                label="Require House Number"
              >
                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
              </Form.Item>
            </TabPane>
          </Tabs>
        </Form>
      </Modal>
    </div>
  );
};

export default LocationConfigurationView;
